import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, SERVICES } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { LogOut, Phone, Mail, MapPin, RefreshCw, Truck } from "lucide-react";

const STATUS_META = {
  new:       { label: "New",             color: "bg-primary/10 text-primary" },
  contacted: { label: "Contacted",       color: "bg-[hsl(210,50%,92%)] text-[hsl(210,60%,30%)]" },
  sent:      { label: "Sent to Hauler",  color: "bg-[hsl(43,80%,90%)] text-[hsl(28,80%,30%)]" },
  sold:      { label: "Sold",            color: "bg-[hsl(145,27%,90%)] text-[hsl(145,27%,24%)]" },
  closed:    { label: "Closed / Lost",   color: "bg-muted text-muted-foreground" },
};
const STATUS_ORDER = ["new", "contacted", "sent", "sold", "closed"];

const serviceLabel = (slug) => SERVICES.find((s) => s.slug === slug)?.title || slug;

const yesNo = (v) => (v === true ? "Yes" : v === false ? "No" : null);

const AdminDashboard = () => {
  const { user, loading, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const fetchAll = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/leads");
      setLeads(data);
    } catch (e) {
      toast.error("Failed to load leads");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (user) fetchAll();
  }, [user]);

  if (loading) return <div className="p-12 text-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/leads/${id}/status`, { status });
      setLeads((prev) => prev.map((l) => (l.id === id ? data : l)));
      toast.success(`Marked as ${STATUS_META[status]?.label || status}`);
    } catch {
      toast.error("Failed to update status");
    }
  };

  const counts = {
    new: leads.filter((l) => l.status === "new").length,
    contacted: leads.filter((l) => l.status === "contacted").length,
    sent: leads.filter((l) => l.status === "sent").length,
    sold: leads.filter((l) => l.status === "sold").length,
  };

  return (
    <div className="min-h-screen bg-muted" data-testid="admin-dashboard">
      <header className="border-b border-border bg-card">
        <div className="container-wide flex h-16 items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-display">
            <span className="flex h-8 w-8 items-center justify-center rounded-sm bg-secondary text-secondary-foreground">
              <Truck className="h-4 w-4" />
            </span>
            <span className="font-black uppercase">Leads Console</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-muted-foreground sm:inline">{user.email}</span>
            <button onClick={fetchAll} className="rounded-sm border border-border bg-white px-3 py-2 text-sm font-medium hover:bg-muted" data-testid="admin-refresh-btn">
              <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            </button>
            <button onClick={logout} className="flex items-center gap-2 rounded-sm border border-border bg-white px-3 py-2 text-sm font-medium hover:bg-muted" data-testid="admin-logout-btn">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="container-wide py-8">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          {[
            { l: "Total leads", v: leads.length },
            { l: "New", v: counts.new },
            { l: "Contacted", v: counts.contacted },
            { l: "Sent to Hauler", v: counts.sent },
            { l: "Sold", v: counts.sold },
          ].map((s) => (
            <div key={s.l} className="rounded-sm border border-border bg-card p-5">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">{s.l}</p>
              <p className="mt-1 font-display text-3xl font-black">{s.v}</p>
            </div>
          ))}
        </div>

        <div className="mt-8" data-testid="leads-tab">

        {(
          <div className="mt-6 overflow-hidden rounded-sm border border-border bg-card" data-testid="leads-table">
            {leads.length === 0 ? (
              <div className="p-10 text-center text-muted-foreground">No leads yet.</div>
            ) : (
              <div className="divide-y divide-border">
                {leads.map((l) => (
                  <div key={l.id} className="grid gap-4 p-5 sm:grid-cols-4" data-testid={`lead-row-${l.id}`}>
                    <div className="sm:col-span-1">
                      <p className="font-display font-bold">{l.name}</p>
                      <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">
                        <Phone className="h-3.5 w-3.5" /> {l.phone}
                      </p>
                      {l.email && (
                        <p className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Mail className="h-3.5 w-3.5" /> {l.email}
                        </p>
                      )}
                      <p className="flex items-center gap-1 text-sm text-muted-foreground">
                        <MapPin className="h-3.5 w-3.5" /> ZIP {l.zip_code}
                      </p>
                    </div>
                    <div className="sm:col-span-2">
                      <p className="text-sm font-semibold text-primary">{serviceLabel(l.service)}</p>
                      {l.address && <p className="text-sm text-muted-foreground">{l.address}</p>}
                      {l.description && <p className="mt-2 text-sm">{l.description}</p>}

                      <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
                        {l.item_location && (
                          <span className="rounded-sm bg-muted px-2 py-0.5 font-semibold capitalize">
                            {l.item_location}
                          </span>
                        )}
                        {yesNo(l.stairs_or_elevator) && (
                          <span className="rounded-sm bg-muted px-2 py-0.5">Stairs/elev: {yesNo(l.stairs_or_elevator)}</span>
                        )}
                        {yesNo(l.heavy_items) && (
                          <span className="rounded-sm bg-muted px-2 py-0.5">Heavy: {yesNo(l.heavy_items)}</span>
                        )}
                        {yesNo(l.construction_debris) && (
                          <span className="rounded-sm bg-muted px-2 py-0.5">Debris: {yesNo(l.construction_debris)}</span>
                        )}
                        {l.preferred_date && (
                          <span className="rounded-sm bg-muted px-2 py-0.5">Pickup: {l.preferred_date}</span>
                        )}
                        {yesNo(l.same_day_needed) === "Yes" && (
                          <span className="rounded-sm bg-primary/15 px-2 py-0.5 font-semibold text-primary">Same-day</span>
                        )}
                      </div>

                      {l.photo_urls && l.photo_urls.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {l.photo_urls.map((u, i) => (
                            <a key={i} href={`${process.env.REACT_APP_BACKEND_URL}${u}`} target="_blank" rel="noreferrer" className="block h-16 w-16 overflow-hidden rounded-sm border border-border" data-testid={`lead-photo-${l.id}-${i}`}>
                              <img src={`${process.env.REACT_APP_BACKEND_URL}${u}`} alt={`lead ${i}`} className="h-full w-full object-cover" />
                            </a>
                          ))}
                        </div>
                      )}
                      <p className="mt-2 text-xs text-muted-foreground">
                        Received {new Date(l.created_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex flex-col items-start gap-2 sm:col-span-1 sm:items-end">
                      <span className={`rounded-sm px-2.5 py-1 text-xs font-semibold uppercase tracking-wider ${STATUS_META[l.status]?.color || "bg-muted"}`}>
                        {STATUS_META[l.status]?.label || l.status}
                      </span>
                      <div className="flex flex-wrap justify-end gap-1">
                        {STATUS_ORDER.map((s) => (
                          <button
                            key={s}
                            onClick={() => updateStatus(l.id, s)}
                            disabled={l.status === s}
                            className={`rounded-sm border px-2 py-1 text-xs font-medium ${l.status === s ? "border-primary text-primary" : "border-border text-muted-foreground hover:border-primary hover:text-primary"}`}
                            data-testid={`lead-status-${l.id}-${s}`}
                          >
                            {STATUS_META[s].label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
